/* eslint-disable max-lines */
import { QuickResolveActionItem } from '@hakimo-ui/hakimo/types';
import { Button, Tooltip } from '@hakimo-ui/shared/ui-base';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import {
  getInfoTextByFieldId,
  renderSubItems,
  renderEmptyState,
} from '../utils';
import {
  getQuickResolveActionLabel,
  hasQuickResolveSubActions,
  isQuickResolveAction,
} from '../type-guards';
import CreateUpdateQuickResolveModal from '../quick-resolve/CreateUpdateQuickResolveModal';
import SOPCard from './SOPCard';

interface Props {
  quickResolveActions: QuickResolveActionItem[];
  onChange: (val: QuickResolveActionItem[]) => void;
  isEditing: boolean;
}

export function SOPQuickResolveCard(props: Props) {
  const { quickResolveActions, onChange, isEditing } = props;

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateItem, setUpdateItem] = useState<QuickResolveActionItem>();
  const [isNewItem, setIsNewItem] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>(
    {}
  );

  const handleAddItem = () => {
    setIsNewItem(true);
    setUpdateItem('');
    setShowUpdateModal(true);
  };

  const handleEditItem = (i: number) => () => {
    setShowUpdateModal(true);
    setUpdateItem(quickResolveActions[i]);
  };

  const handleDeleteItem = (i: number) => () => {
    const updatedItems = [...quickResolveActions];
    updatedItems.splice(i, 1);
    onChange(updatedItems);
  };

  const handleCloseModal = () => {
    setIsNewItem(false);
    setShowUpdateModal(false);
    setUpdateItem(undefined);
  };

  const handleUpdateFromModal = (val: QuickResolveActionItem) => {
    if (updateItem === undefined) {
      return;
    }
    const updatedItems = [...quickResolveActions];
    if (isNewItem) {
      updatedItems.push(val);
    } else {
      updatedItems[quickResolveActions.indexOf(updateItem)] = val;
    }
    onChange(updatedItems);
    handleCloseModal();
  };

  const toggleExpanded = (index: number) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const headerAction = isEditing ? (
    <Button
      variant="primary"
      onClick={handleAddItem}
      className="flex items-center gap-2"
    >
      <PlusIcon className="h-4 w-4" />
      Add
    </Button>
  ) : null;

  const headerInfo = (
    <Tooltip
      text={getInfoTextByFieldId('quickResolveActions') || ''}
      size="large"
      colorModifier="info"
    >
      <InformationCircleIcon className="h-5 w-5" />
    </Tooltip>
  );

  return (
    <>
      <SOPCard
        title="Quick Resolve Actions"
        headerAction={headerAction}
        headerInfo={headerInfo}
      >
        {isEditing ? (
          <div className="flex flex-wrap gap-4">
            {quickResolveActions.map((item, i) => (
              <div
                className="dark:bg-ondark-bg-2 bg-onlight-bg-2 border-onlight-line-2 dark:border-ondark-line-2 flex flex-col gap-2 rounded-md border p-2 text-sm"
                key={i}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {getQuickResolveActionLabel(item)}
                  </span>
                  <div className="flex items-center">
                    <Button variant="icon" onClick={handleEditItem(i)}>
                      <PencilIcon className="h-5 w-5" />
                    </Button>
                    <Button variant="icon" onClick={handleDeleteItem(i)}>
                      <TrashIcon className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
                {hasQuickResolveSubActions(item) &&
                  isQuickResolveAction(item) &&
                  item.subActions &&
                  item.subActions.length > 0 &&
                  renderSubItems(item.subActions, expandedItems[i], () =>
                    toggleExpanded(i)
                  )}
              </div>
            ))}
          </div>
        ) : quickResolveActions && quickResolveActions.length > 0 ? (
          <div className="flex flex-wrap gap-4">
            {quickResolveActions.map((item, i) => (
              <div
                className="dark:bg-ondark-bg-2 bg-onlight-bg-2 border-onlight-line-2 dark:border-ondark-line-2 flex flex-col gap-2 rounded-md border p-2 text-sm"
                key={i}
              >
                <span className="font-medium">
                  {getQuickResolveActionLabel(item)}
                </span>
                {hasQuickResolveSubActions(item) &&
                  isQuickResolveAction(item) &&
                  item.subActions &&
                  item.subActions.length > 0 &&
                  renderSubItems(item.subActions, expandedItems[i], () =>
                    toggleExpanded(i)
                  )}
              </div>
            ))}
          </div>
        ) : (
          renderEmptyState()
        )}
      </SOPCard>
      {showUpdateModal && updateItem !== undefined && (
        <CreateUpdateQuickResolveModal
          onClose={handleCloseModal}
          action={updateItem}
          onSubmit={handleUpdateFromModal}
          isNew={isNewItem}
        />
      )}
    </>
  );
}

export default SOPQuickResolveCard;
